/**
 * MIDI Service for handling Web MIDI API connections and note events
 */

class MidiService {
  constructor() {
    this.midiAccess = null;
    this.selectedInput = null;
    this.listeners = new Set();
    this.isConnected = false;
    this.noteBuffer = [];
    this.startTime = null;
    this.activeNotes = new Map(); // Track active notes for duration calculation
  }

  /**
   * Initialize Web MIDI API
   */
  async initialize() {
    try {
      if (!navigator.requestMIDIAccess) {
        // Check if this is iOS
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
          throw new Error('Web MIDI API is not supported on iOS devices. All browsers on iOS use WebKit which lacks Web MIDI support. Please use a desktop computer with Chrome, Edge, or Opera.');
        }
        throw new Error('Web MIDI API is not supported in this browser. Please use Chrome, Edge, or Opera.');
      }

      // Request MIDI access - this will trigger browser permission prompt
      this.midiAccess = await navigator.requestMIDIAccess({ sysex: false });
      
      // Listen for device changes
      this.midiAccess.onstatechange = (e) => {
        this.notifyListeners('deviceChange', {
          port: e.port,
          state: e.port.state,
          connection: e.port.connection
        });
      };

      return true;
    } catch (error) {
      console.error('Failed to initialize MIDI:', error);
      
      // Check if this is iOS and provide specific message
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
        throw new Error('Web MIDI API is not supported on iOS devices. All browsers on iOS use WebKit which lacks Web MIDI support. Please use a desktop computer with Chrome, Edge, or Opera.');
      }
      
      // Provide more helpful error messages
      if (error.name === 'SecurityError') {
        throw new Error('MIDI access was denied. Please allow MIDI access in your browser settings and refresh the page.');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Web MIDI API is not supported in this browser. Please use Chrome, Edge, or Opera.');
      } else if (error.message.includes('permission')) {
        throw new Error('MIDI permission required. Please allow MIDI access when prompted by your browser.');
      }
      
      throw new Error(`MIDI initialization failed: ${error.message}`);
    }
  }

  /**
   * Get available MIDI input devices
   */
  getInputDevices() {
    if (!this.midiAccess) {
      return [];
    }

    const devices = [];
    for (const input of this.midiAccess.inputs.values()) {
      devices.push({
        id: input.id,
        name: input.name,
        manufacturer: input.manufacturer,
        state: input.state,
        connection: input.connection
      });
    }
    return devices;
  }

  /**
   * Connect to a specific MIDI input device
   */
  connectToDevice(deviceId) {
    if (!this.midiAccess) {
      throw new Error('MIDI not initialized');
    }

    // Disconnect from current device if any
    if (this.selectedInput) {
      this.disconnect();
    }

    const input = this.midiAccess.inputs.get(deviceId);
    if (!input) {
      throw new Error('MIDI device not found');
    }

    this.selectedInput = input;
    this.isConnected = true;
    this.startTime = performance.now();
    this.noteBuffer = [];

    // Set up message handler
    this.selectedInput.onmidimessage = (message) => {
      this.handleMidiMessage(message);
    };

    this.notifyListeners('connected', {
      device: {
        id: input.id,
        name: input.name
      }
    });

    return true;
  }

  /**
   * Disconnect from current MIDI device
   */
  disconnect() {
    if (this.selectedInput) {
      this.selectedInput.onmidimessage = null;
      this.selectedInput = null;
      this.isConnected = false;
      // DON'T clear the buffer here - we need it for analysis!
      // this.noteBuffer = [];
      this.startTime = null;

      this.notifyListeners('disconnected', {});
    }
  }

  /**
   * Handle incoming MIDI messages
   */
  handleMidiMessage(message) {
    const [status, note, velocity] = message.data;
    
    // Filter out MIDI system messages (0xF0-0xFF)
    // These include timing clock (0xF8), active sensing (0xFE), etc.
    if (status >= 0xF0) {
      // Ignore system messages - they're not notes
      return;
    }
    
    const timestamp = (performance.now() - this.startTime) / 1000; // Convert to seconds

    // Raw MIDI data processed (logging removed)

    // Parse MIDI message type
    const messageType = status >> 4;
    const channel = status & 0x0f;

    let eventType = null;
    let noteData = null;

    // Note On (0x9) or Note Off (0x8)
    if (messageType === 0x9 && velocity > 0) {
      eventType = 'note_on';
      noteData = {
        note,
        velocity,
        timestamp,
        channel,
        event_type: 'note_on',
        hand: null, // Will be classified by backend
        finger: null,
        duration: null // Will be calculated when note_off is received
      };
      
      // Track this note as active
      const noteKey = `${note}_${channel}`;
      this.activeNotes.set(noteKey, {
        startTime: timestamp,
        noteData: noteData,
        bufferIndex: this.noteBuffer.length // Track position in buffer
      });
      
      // Note ON tracked in active notes
      
    } else if (messageType === 0x8 || (messageType === 0x9 && velocity === 0)) {
      eventType = 'note_off';
      
      // Find the corresponding note_on and calculate duration
      const noteKey = `${note}_${channel}`;
      const activeNote = this.activeNotes.get(noteKey);
      
      if (activeNote) {
        // Calculate duration
        const duration = timestamp - activeNote.startTime;
        
        // Note OFF: calculate duration and update buffer
        
        // Update the original note_on event with the duration
        if (activeNote.bufferIndex < this.noteBuffer.length) {
          this.noteBuffer[activeNote.bufferIndex].duration = duration;
        }
        
        // Clean up
        this.activeNotes.delete(noteKey);
        
        // Create note_off event with duration info
        noteData = {
          note,
          velocity: 0,
          timestamp,
          channel,
          event_type: 'note_off',
          hand: null,
          finger: null,
          duration: duration,
          note_on_timestamp: activeNote.startTime // Reference to the note_on event
        };
      } else {
        // No matching note_on found (might have started before recording)
        noteData = {
          note,
          velocity: 0,
          timestamp,
          channel,
          event_type: 'note_off',
          hand: null,
          finger: null,
          duration: null
        };
      }
    }

    if (noteData) {
      // Add to buffer
      this.noteBuffer.push(noteData);

      // Notify listeners
      this.notifyListeners('note', noteData);

      // Also notify with raw MIDI data for debugging
      this.notifyListeners('midiMessage', {
        data: message.data,
        timestamp: message.timeStamp
      });
    }
  }

  /**
   * Get all notes in the buffer
   */
  getNoteBuffer() {
    return [...this.noteBuffer];
  }

  /**
   * Clear the note buffer
   */
  clearNoteBuffer() {
    this.noteBuffer = [];
    this.activeNotes.clear(); // Clear active notes tracking
    this.startTime = performance.now();
  }

  /**
   * Get notes since a specific timestamp
   */
  getNotesSince(timestamp) {
    return this.noteBuffer.filter(note => note.timestamp >= timestamp);
  }

  /**
   * Add event listener
   */
  addEventListener(listener) {
    this.listeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener) {
    this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of an event
   */
  notifyListeners(eventType, data) {
    for (const listener of this.listeners) {
      try {
        listener(eventType, data);
      } catch (error) {
        console.error('Error in MIDI listener:', error);
      }
    }
  }

  /**
   * Check if Web MIDI API is supported
   */
  static isSupported() {
    return 'requestMIDIAccess' in navigator;
  }

  /**
   * Simulate MIDI message for demo/testing purposes
   * @param {Object} noteData - Note data with { note, velocity, timestamp, channel, hand }
   */
  simulateMidiMessage(noteData) {
    // Simulate a note_on event
    const simulatedNote = {
      note: noteData.note,
      velocity: noteData.velocity,
      timestamp: noteData.timestamp,
      channel: noteData.channel || 0,
      event_type: 'note_on',
      hand: noteData.hand || null,
      finger: null,
      duration: noteData.duration || 0.2
    };
    
    // Track as active note
    const noteKey = `${noteData.note}_${noteData.channel || 0}`;
    this.activeNotes.set(noteKey, {
      startTime: noteData.timestamp,
      noteData: simulatedNote,
      bufferIndex: this.noteBuffer.length
    });
    
    // Add to buffer
    this.noteBuffer.push(simulatedNote);
    
    // Notify listeners
    this.notifyListeners('note', simulatedNote);
    
    // Schedule note_off after duration
    if (noteData.duration) {
      const noteOffTimestamp = noteData.timestamp + noteData.duration;
      
      // Simulate note_off
      const noteOffData = {
        note: noteData.note,
        velocity: 0,
        timestamp: noteOffTimestamp,
        channel: noteData.channel || 0,
        event_type: 'note_off',
        hand: noteData.hand || null,
        finger: null,
        duration: noteData.duration,
        note_on_timestamp: noteData.timestamp
      };
      
      // Update duration in the original note_on
      if (this.activeNotes.has(noteKey)) {
        const activeNote = this.activeNotes.get(noteKey);
        if (activeNote.bufferIndex < this.noteBuffer.length) {
          this.noteBuffer[activeNote.bufferIndex].duration = noteData.duration;
        }
        this.activeNotes.delete(noteKey);
      }
      
      this.noteBuffer.push(noteOffData);
      this.notifyListeners('note', noteOffData);
    }
  }

  /**
   * Play back a sequence of notes from CSV data
   * @param {Array} notes - Array of note objects from CSV
   * @param {Function} onProgress - Callback for playback progress (0-1)
   * @param {Function} onComplete - Callback when playback completes
   * @returns {Object} - Object with stop() method to cancel playback
   */
  playbackCSVNotes(notes, onProgress = null, onComplete = null) {
    if (!notes || notes.length === 0) {
      console.error('No notes to play back');
      return { stop: () => {} };
    }
    
    // Reset state for new playback
    this.clearNoteBuffer();
    this.startTime = performance.now();
    this.isConnected = true; // Mark as "connected" for playback
    
    // Sort notes by timestamp
    const sortedNotes = [...notes].sort((a, b) => a.timestamp - b.timestamp);
    const startTimestamp = sortedNotes[0].timestamp;
    const endTimestamp = sortedNotes[sortedNotes.length - 1].timestamp;
    const totalDuration = endTimestamp - startTimestamp;
    
    console.log(`CSV Playback: ${notes.length} notes over ${totalDuration.toFixed(2)}s`);
    
    const timeouts = [];
    let cancelled = false;
    
    // Schedule each note
    sortedNotes.forEach((note, index) => {
      const delay = (note.timestamp - startTimestamp) * 1000; // Convert to milliseconds
      
      const timeoutId = setTimeout(() => {
        if (!cancelled) {
          // Adjust timestamp relative to playback start
          const playbackNote = {
            ...note,
            timestamp: (performance.now() - this.startTime) / 1000
          };
          
          this.simulateMidiMessage(playbackNote);
          
          // Report progress
          if (onProgress) {
            const progress = (note.timestamp - startTimestamp) / totalDuration;
            onProgress(progress);
          }
          
          // Check if this is the last note
          if (index === sortedNotes.length - 1 && onComplete) {
            // Wait a bit for the last note to finish before calling complete
            setTimeout(() => {
              if (!cancelled && onComplete) {
                onComplete();
              }
            }, (note.duration || 0.1) * 1000 + 100);
          }
        }
      }, delay);
      
      timeouts.push(timeoutId);
    });
    
    // Return control object
    return {
      stop: () => {
        cancelled = true;
        timeouts.forEach(id => clearTimeout(id));
        console.log('CSV Playback stopped');
      }
    };
  }

  /**
   * Get browser-specific setup instructions
   */
  static getBrowserInstructions() {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Check for iOS first (all browsers on iOS use WebKit and don't support Web MIDI)
    if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
      return {
        browser: 'iOS (Safari/Chrome)',
        instructions: [
          'Web MIDI API is not supported on iOS devices.',
          'All browsers on iOS use WebKit which lacks Web MIDI support.',
          '',
          '📱 Mobile Solutions:',
          '• Use a desktop computer with Chrome, Edge, or Opera',
          '• Try the "Web MIDI Browser" app from the App Store',
          '• Use an Android device with Chrome browser',
          '',
          '💡 The Web MIDI Browser app enables MIDI support on iOS!'
        ]
      };
    }
    
    if (userAgent.includes('firefox')) {
      return {
        browser: 'Firefox',
        instructions: [
          '1. Type "about:config" in the address bar',
          '2. Search for "dom.webmidi.enabled"',
          '3. Set it to "true"',
          '4. Restart Firefox and refresh this page'
        ]
      };
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      return {
        browser: 'Safari',
        instructions: [
          '1. Safari menu → Preferences → Advanced',
          '2. Check "Show Develop menu in menu bar"',
          '3. Develop menu → Experimental Features',
          '4. Enable "Web MIDI API"'
        ]
      };
    } else {
      return {
        browser: 'Chrome/Edge',
        instructions: [
          '1. Click the 🔒 lock icon in your address bar',
          '2. Set "MIDI devices" to "Allow"',
          '3. Refresh this page'
        ]
      };
    }
  }

  /**
   * Get MIDI note name from note number
   */
  static getNoteName(noteNumber) {
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const octave = Math.floor(noteNumber / 12) - 1;
    const noteName = noteNames[noteNumber % 12];
    return `${noteName}${octave}`;
  }

  /**
   * Convert note buffer to format expected by backend
   */
  formatNotesForBackend() {
    // Filter to only include note_on events with their durations
    // Backend will process these for analysis
    const notes = this.noteBuffer
      .filter(note => note.event_type === 'note_on')
      .map(note => ({
        note: note.note,
        velocity: note.velocity,
        timestamp: note.timestamp,
        duration: note.duration || 0.2, // Default 200ms if no note_off received
        channel: note.channel,
        hand: note.hand,
        finger: note.finger,
        event_type: note.event_type
      }));
    
    // Format notes for backend analysis (logging removed)
    
    return notes;
  }
}

// Create singleton instance
const midiService = new MidiService();

export { MidiService };
export default midiService;
